import React from 'react'
import {
  TableCell,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
} from '@resiliatech/birdsong'
import styled from '@emotion/styled'
import { analytics } from 'Modules/Analytics'
import { team } from 'Modules/Analytics/trackActionInterface/Account/team'
import {
  FunderOrgToUserRole,
  NonprofitOrgToUserRole,
  User,
} from 'Generated/graphql-types'
import GET_NONPROFIT_ORG_CONFIG_QUERY from 'Apollo/Queries/GetNonprofitOrgConfig'
import { useAppState } from 'Context/AppContext/AppContext'
import GET_FUNDER_USERS from 'Apollo/Queries/GetFunderUsers'

type Props = {
  resiliaRole: NonprofitOrgToUserRole | FunderOrgToUserRole
  updateUserSettings: (userSettings: any) => void
  user: User
  setShowBirdsongSnackbar: (value: React.SetStateAction<boolean>) => void
  setBirdsongSnackbarMessage: (value: React.SetStateAction<string>) => void
  isFunder?: boolean
}

export const RoleCell = ({
  resiliaRole,
  updateUserSettings,
  user,
  setShowBirdsongSnackbar,
  setBirdsongSnackbarMessage,
  isFunder = false,
}: Props) => {
  const {
    userConfig: { permissions, id: currentUserId, email },
    nonprofitOrgConfig: { orgId: nonprofitOrgId, platformPlan },
    funderOrgConfig: { orgId: funderOrgId },
  } = useAppState()

  const isSelf = user.id === currentUserId

  const [role, setRole] = React.useState<
    NonprofitOrgToUserRole | FunderOrgToUserRole
  >(
    resiliaRole ||
      (isFunder ? FunderOrgToUserRole.Member : NonprofitOrgToUserRole.Staff)
  )

  const dispatchEditRoleSubmitted = analytics.trackActionCreator(
    team.Edit_Role_Submitted
  )

  const onError = () => {
    setShowBirdsongSnackbar(true)
    setBirdsongSnackbarMessage(
      'Unable to update role, please contact support@resilia.com'
    )
  }

  const handleChange = async (event: SelectChangeEvent<unknown>) => {
    setRole(event.target.value as NonprofitOrgToUserRole)
    const userSettingsInfo = {
      firstName: user.firstName,
      lastName: user.lastName,
      role: event.target.value as NonprofitOrgToUserRole,
    }

    await updateUserSettings({
      variables: {
        nonprofitOrgId,
        userIdToUpdate: user.id,
        userSettingsInfo,
      },
      refetchQueries: [
        {
          query: GET_NONPROFIT_ORG_CONFIG_QUERY,
          variables: { nonprofitOrgId },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        setBirdsongSnackbarMessage('Role updated')
        setShowBirdsongSnackbar(true)
        dispatchEditRoleSubmitted({
          userId: currentUserId, // unsure if we need to use the current user id or the one who got updated
          email,
          nonprofitOrgId,
          funderOrgId,
          platformPlan,
          chosenRole: event.target.value as NonprofitOrgToUserRole,
        })
      },
      onError,
    })
  }

  const handleFunderChange = async (event: SelectChangeEvent<unknown>) => {
    setRole(event.target.value as FunderOrgToUserRole)
    await updateUserSettings({
      variables: {
        funderId: funderOrgId,
        userIdToUpdate: user.id,
        userSettingsInfo: {
          firstName: user.firstName,
          lastName: user.lastName,
          role: event.target.value as FunderOrgToUserRole,
        },
      },
      refetchQueries: [
        {
          query: GET_FUNDER_USERS,
          variables: { funderOrgId },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        setBirdsongSnackbarMessage('Role updated')
        setShowBirdsongSnackbar(true)
        // TODO: add with segment events
        // dispatchEditRoleSubmitted({
        //   userId: currentUserId, // unsure if we need to use the current user id or the one who got updated
        //   email,
        //   nonprofitOrgId,
        //   funderOrgId,
        //   platformPlan,
        //   chosenRole: event.target.value as NonprofitOrgToUserRole,
        // })
      },
      onError,
    })
  }

  const resiliaRoles = isFunder
    ? [
        {
          value: FunderOrgToUserRole.Admin,
          label: 'Admin',
          subtitle: '',
        },
        {
          value: FunderOrgToUserRole.Member,
          label: 'Member',
          subtitle: '',
        },
      ]
    : [
        {
          value: NonprofitOrgToUserRole.Admin,
          label: 'Admin',
          subtitle:
            'Access to all of the Resilia platform and platform-wide settings.',
        },
        {
          value: NonprofitOrgToUserRole.Staff,
          label: 'Member',
          subtitle: 'Access to most of the Resilia platform.',
        },
      ]

  if (resiliaRole === NonprofitOrgToUserRole.Owner) {
    ;<Chip label={resiliaRole} variant='filled' size='small' />
  }

  return (
    <TableCell>
      {permissions.includes('team:update') &&
      !isSelf &&
      resiliaRole !== NonprofitOrgToUserRole.Owner ? (
        <RoleSelect
          id='role'
          value={role}
          onChange={(e) => (isFunder ? handleFunderChange(e) : handleChange(e))}
          size='small'
          aria-label='Role Selection'
        >
          {resiliaRoles.map((roleItem) => (
            <StyledMenuItem value={roleItem.value} key={roleItem.value}>
              <div>{roleItem.label}</div>
              <div className='context'>{roleItem.subtitle}</div>
            </StyledMenuItem>
          ))}
        </RoleSelect>
      ) : (
        <Chip
          label={
            resiliaRole === NonprofitOrgToUserRole.Owner
              ? 'Owner'
              : resiliaRoles.find((item) => item.value === resiliaRole)?.label
          }
          variant='filled'
          size='small'
        />
      )}
    </TableCell>
  )
}

const RoleSelect = styled(Select)`
  width: 232px;
  & .context {
    display: none;
  }
  & li {
    flex-direction: column;
    align-content: flex-start;
  }
`

const StyledMenuItem = styled(MenuItem)`
  & .context {
    font-size: 12px;
    white-space: initial;
  }
  &.MuiMenuItem-root {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`
